import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { UnitFormDto, type UnitFormInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-form.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import type { SerdeDtoMetadataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-dto-metadata.interface';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { UnitService } from '@zaoblako/web/core/services/finance/unit.service';
import { DirectoryCommonHistoryComponent } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/directory-common-history.component';
import { DirectoryCommonHistoryTypeEnum } from '@zaoblako/web/modules/directory/dumbs/directory-common-history/enums/directory-common-history-type.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ColorPickerComponent } from '@zaoblako/web/shared/dumbs/color-picker/color-picker.component';
import { UserAndGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-and-group-multi-select/user-and-group-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TenantService } from '@zaoblako/web/core/services/security/tenant.service';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';

interface UnitFormExtended extends UnitFormInterface {
  deleted: boolean;
}

interface UnitUserItem {
  user?: UserFindInterface;
  group?: UserGroupFindInterface;
}

@Component({
  standalone: true,
  selector: 'crm-unit-form',
  templateUrl: './unit-form.component.html',
  styleUrls: ['./unit-form.component.scss'],
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    FormsModule,
    TagModule,
    ButtonComponent,
    SpinnerComponent,
    DirectoryCommonHistoryComponent,
    ColorPickerComponent,
    CheckboxModule,
    UserAndGroupMultiSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnitFormComponent extends BaseFormComponent<UnitFormExtended> implements OnInit {
  @Input() public readonly index: number;
  public unit: UnitFormExtended = {
    name: '',
    short: '',
    color: null,
    deleted: false
  };

  public dto: SerdeDtoClass<UnitFormExtended> = UnitFormDto as any;
  public metadata: SerdeDtoMetadataInterface<UnitFormExtended> =
    UnitFormDto.getMetadata() as SerdeDtoMetadataInterface<UnitFormExtended>;

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public canUploadAtrium = false;

  public unitUsers: UnitUserItem[] = [];

  public get DirectoryCommonHistoryTypeEnum() {
    return DirectoryCommonHistoryTypeEnum;
  }

  public constructor(
    public readonly unitService: UnitService,
    public readonly formStoreService: FormStoreService,
    private readonly tenantService: TenantService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.tenantService
      .isConnectedToAtrium()
      .subscribe({
        next: (canUploadAtrium) => {
          this.canUploadAtrium = canUploadAtrium;
          this.cdRef.markForCheck();
        }
      });


    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.unitService
      .findById<UnitFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        withDeleted: true,
        select: [
          'id',
          'deleted',
          'name',
          'short',
          'users',
          'color',
          'distributeCosts'
        ]
      })
      .subscribe({
        next: (unit) => {
          this.unit = unit;
          if (this.unit.users?.length) {
            this.unit.users.forEach((user: UserFindInterface) => this.unitUsers.push({ user }));
          }
          if (this.unit.userGroups?.length) {
            this.unit.userGroups.forEach((group: UserGroupFindInterface) => this.unitUsers.push({ group }));
          }
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveUnit() {
    this.normalizeUnitColor();
    if (this.checkValidate(this.unit).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.unitService.update<UnitFindInterface, UnitFindInterface>(this.unit).subscribe({
          next: (unit) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Бизнес единица "${this.unit.name}" обновлена`);
            this.formStoreService.saveNext(unit, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.unitService.create<UnitFindInterface>(this.unit).subscribe({
          next: (unit) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Бизнес единица создана');
            this.formStoreService.saveNext(unit, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteUnit() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить бизнес единицу "${this.unit.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.unitService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Бизнес единица "${this.unit.name}" удалена`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreUnit() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить бизнес единицу "${this.unit.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.unitService.restore(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.unit = { ...this.unit, deleted: false };
            this.cdRef.markForCheck();
            this.errorService.addSuccessMessage(`Бизнес единица "${this.unit.name}" восстановлена`);
            this.formStoreService.saveNext(this.unit, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public changeColor(color: string | null | undefined): void {
    const value = color?.trim() ?? '';
    if (!value) {
      this.unit.color = null;
      this.cdRef.markForCheck();
      return;
    }

    this.unit.color = value[0] !== '#' ? `#${value}` : value;
    this.cdRef.markForCheck();
  }

  public onColorPickerChange(color: string): void {
    const value = color?.trim() ?? '';
    if (!value) {
      this.unit.color = null;
    } else {
      this.unit.color = value[0] !== '#' ? `#${value}` : value;
    }
    this.cdRef.markForCheck();
  }

  public onChangeUsersAndGroup(): void {
    this.unit.users = [];
    this.unit.userGroups = [];

    this.unitUsers.forEach((unitUser) => {
      unitUser.user ? this.unit.users.push(unitUser.user) : this.unit.userGroups.push(unitUser.group);
    });
  }

  private normalizeUnitColor(): void {
    const trimmed = this.unit.color?.trim();
    if (!trimmed) {
      this.unit.color = null;
      return;
    }

    this.unit.color = trimmed[0] !== '#' ? `#${trimmed}` : trimmed;
  }
}
